import axios from 'axios'
import React, { useState, useContext, useEffect } from 'react'
import Button from '../../../button/Button'
import DropDown from 'src/components/dropDown/Dropdown'
import Input from '../../../input/Input'
import { baseApiURL } from 'src/utils/utils'
import { IState } from 'src/Interface'
import { StateContext } from '../../../../AppContext'
import styles from './add-user.module.scss'
import { isValidNumberForRegion } from 'libphonenumber-js'
import { toast } from 'react-toastify'
import { validEmail, validString } from 'src/utils/utils'
import { DispatchContext } from 'src/AppContext'


interface IErrors {
  name?: boolean
  lastname?: boolean
  phone?: boolean
  email: boolean
}

interface Props {
  close: () => void
  companyId: number
  branchId: number
}

const AddUser: React.FunctionComponent<Props> = ({ close, companyId, branchId }: Props) => {
  const state: IState = useContext(StateContext)
  const [role, setRole] = useState('')
  const [roles, setRoles] = useState([])
  const dispatch = useContext(DispatchContext)
  const [isLoading, setIsLoading] = useState(false)
  const [values, setValues] = useState<any>(JSON.parse(window.sessionStorage.getItem('users')) || {
    name: '',
    lastname: '',
    phone: '',
    email: ''
  })

  const [validationErrors, setValidationErrors] = useState<IErrors>({
    name: false,
    lastname: false,
    phone: false,
    email: false
  })

  const [errors, setErrors] = useState<IErrors>({
    email: false,
    phone: false
  })

  const handleInputChange = (fieldName: string) => (value: string) => {
    window.sessionStorage.setItem('users', JSON.stringify({
      ...values,
      [fieldName]: value
    }))

    setValues({
      ...values,
      [fieldName]: value
    })
  }

  const resetError = (fieldName: string) => {
    setErrors({
      ...errors,
      [fieldName]: false
    })
    setValidationErrors({
      ...validationErrors,
      [fieldName]: false
    })
  }

  const handleValidation = (regexp: RegExp, fieldName: string) => (value: string) => {
    let error = false

    if (fieldName === 'phone') {
      if (!isValidNumberForRegion(value, 'DK')) {
        error = true
      }
    } else {
      if (value && regexp && !regexp.test(value || '')) {
        error = true
      }
    }

    setValidationErrors({
      ...validationErrors,
      [fieldName]: error
    })
  }


  const createUser = () => {
    setIsLoading(true)
    axios.post(`${baseApiURL()}/users`, {
      firstName: values.name,
      lastName: values.lastname,
      phone: values.phone ? values.phone : null,
      email: values.email,
      branchId: branchId,
      role
    }, {
      headers: {
        Authorization: `Bearer ${state.user.accessToken}`
      }
    }).then((result) => {
      return result.data.userId
    }).then((userId) => {
      return axios(`${baseApiURL()}/users/${userId}`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      })
    }).then((result) => {
      setIsLoading(false)
      window.sessionStorage.removeItem('users')
      close()
      dispatch({ type: 'SET_USERS', users: [result.data, ...state.users] })
      toast('Medarbejder oprettet')
    }).catch(error => {
      setIsLoading(false)
      if (error.response.data.message.startsWith('Key (email)=(')) {
        setErrors({
          ...error,
          email: true
        })
      } else if (error.response.data.message.startsWith('invalid phone number')) {
        setErrors({
          ...error,
          phone: true
        })
      } else if (error.response.data.message.startsWith('Key (company_id, code)=')) {
        setErrors({
          ...error,
          code: true
        })
      } else {
        if (error.response) {
          toast(error.response.data.message)
        } else {
          toast('Der gik desværre noget galt. Prøv igen om lidt')
        }
      }
    })
  }

  useEffect(() => {
    switch (state.user.role) {
      case 'DanTaxiAdmin':
        if (state.user.companyId === Number(companyId)) {
          setRole('DanTaxiAdmin')
          setRoles([
            {
              label: 'DanTaxiAdmin',
              value: 'DanTaxiAdmin'
            }
          ])
        } else {
          setRole('BranchEmployee')
          setRoles([
            {
              label: 'Bruger',
              value: 'BranchEmployee'
            },
            {
              label: 'Manager',
              value: 'BranchAdmin'
            },
            {
              label: 'Admin',
              value: 'CompanyAdmin'
            }
          ]
          )
        }
        break;
      case 'CompanyAdmin':
        setRole('BranchEmployee')
        setRoles([
          {
            label: 'Bruger',
            value: 'BranchEmployee'
          },
          {
            label: 'Manager',
            value: 'BranchAdmin',
          },
          {
            label: 'Admin',
            value: 'CompanyAdmin'
          }
        ])
        break;
      case 'BranchAdmin':
        setRole('BranchEmployee')
        setRoles([
          {
            label: 'Bruger',
            value: 'BranchEmployee'
          },
          {
            label: 'Manager',
            value: 'BranchAdmin',
          },
        ])
        break;
      default:
        break;
    }
  }, [companyId, state.user.companyId, state.user.role])


  if (!roles.length) {
    return null
  }

  return (
    <div className={styles.addUser}>
      <header className={styles.header}>
        <h4>Opret medarbejder</h4>
      </header>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className='tw-mb-3'>
            <Input
              label='Fornavn *'
              type='text'
              value={values.name}
              error={validationErrors.name}
              resetError={() => resetError('name')}
              handleValidation={() => handleValidation(validString, 'name')}
              handleInputChange={() => handleInputChange('name')}
              name='firstname'
              placeholder='Navn'
            />
            {validationErrors.name && <p className='errorMsg'>Fornavn bør være mellem 2 og 32 karakterer</p>}
          </div>
          <div className='tw-mb-3'>
            <Input
              label='Efternavn *'
              type='text'
              value={values.lastname}
              error={validationErrors.lastname}
              resetError={() => resetError('lastname')}
              handleValidation={() => handleValidation(validString, 'lastname')}
              handleInputChange={() => handleInputChange('lastname')}
              name='lastname'
              placeholder='Efternavn'
            />
            {validationErrors.lastname && <p className='errorMsg'>Efternavn bør være mellem 2 og 32 karakterer</p>}
          </div>

          <div className="tw-flex">
            <div className='tw-flex-1'>
              <Input
                label='Telefon'
                countryCode='+45'
                error={validationErrors.phone}
                value={values.phone}
                resetError={() => resetError('phone')}
                type='text'
                isAllowOnlyDigits
                name='mobile'
                handleValidation={() => handleValidation(null, 'phone')}
                handleInputChange={() => handleInputChange('phone')}
                placeholder='Indtast nr.'
              />
              {validationErrors.phone && <p className='errorMsg'>Telefon bør kun bestå af tal og være 8 karakterer.</p>}
              {errors.phone && <p className='errorMsg'>Ugyldigt telefonnummer</p>}
            </div>
            <div className='tw-flex-1 tw-ml-3'>
              <Input
                label='E-mail *'
                error={errors.email || validationErrors.email}
                value={values.email}
                resetError={() => resetError('email')}
                type='text'
                handleValidation={() => handleValidation(validEmail, 'email')}
                handleInputChange={() => handleInputChange('email')}
                name='email'
                placeholder='indtast e-mail'
              />
              {validationErrors.email && <p className='errorMsg'>Emailen ser ikke ud til at være i et korrekt format.</p>}
              {errors.email && <p className='errorMsg'>Dette email eksisterer allerede.</p>}
            </div>
          </div>
        </div>
        <div className={styles.section}>
          <DropDown label='Rettigheder *' handleOnChange={setRole} options={roles} value={role} />
        </div>
      </div>
      <footer className={styles.footer}>
        <Button type='secondary' label='Annuller' handleClick={close} />
        <Button disabled={(!(values.name && values.lastname && values.email && role) || !!Object.values(validationErrors).filter(error => error).length)} loading={isLoading} type='primary' label='Opret bruger' handleClick={createUser} />
      </footer>
    </div>
  )
}

export default AddUser
